import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiMutation } from '../useApiMutation';
import { bookingKeys, schedulerKeys } from '@/lib/queryKeys';
import { schedulerEndpoints } from '@/lib/api/endpoints';
import { apiDelete, operationsClient } from '@/lib/api';
import type { BookingListResponse } from '@/types/booking';

export function useCancelBooking() {
  const queryClient = useQueryClient();

  return useApiMutation<void, string>({
    mutationFn: (id) => apiDelete<void>(operationsClient, schedulerEndpoints.booking(id)),
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: bookingKeys.lists() });
      const snapshots = new Map<string, BookingListResponse>();
      queryClient
        .getQueriesData<BookingListResponse>({ queryKey: bookingKeys.lists() })
        .forEach(([key, data]) => {
          if (data) {
            snapshots.set(JSON.stringify(key), data);
            queryClient.setQueryData<BookingListResponse>(key as string[], {
              ...data,
              bookings: data.bookings.filter((b) => b.id !== id),
              total: Math.max(0, data.total - 1),
            });
          }
        });
      return { snapshots };
    },
    onError: (error, id, context) => {
      const ctx = context as { snapshots?: Map<string, BookingListResponse> } | undefined;
      ctx?.snapshots?.forEach((data, keyStr) => {
        queryClient.setQueryData(JSON.parse(keyStr) as string[], data);
      });
      toast.error(error instanceof Error ? error.message : 'Failed to cancel service call');
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: bookingKeys.all() });
      void queryClient.invalidateQueries({ queryKey: schedulerKeys.all() });
      toast.success('Service call cancelled');
    },
  });
}
